/**
 * Global Templates pane – system admin view for global cycle templates.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { translateUi } from '../i18n/index';
import { Trash2, Edit2, Plus, FileText, X, Clock } from "../icons";
import { TRAIN_DRIVER_V1_CYCLE } from "../cycles/trainDriverV1";
import { GRADING_LEVELS, type Cycle } from "../cycles/types";
import { CycleStructureView } from "../CycleStructureView";
import { cb, cbCn } from "./cycleBuilderTheme";
import { formatCycleDurationLabel, resolveDisplayedCycleMonths } from "./formatCycleDuration";
import { IndustryStandardUnitsPane } from "./IndustryStandardUnitsPane";
import { CustomCycleCriteriaEditor } from "./CustomCycleCriteriaEditor";
import { CollapsiblePane } from "./CollapsiblePane";
import { CycleCountsBadge } from "./CycleCountsBadge";
import type { WizardCriteriaItem } from "./CycleWizardStore";
import {
  buildTemplateLibraryKeyCounts,
  countWizardCycleStats,
  flattenLibraryUnitsToWizardItems,
  libraryUnitsLookIndustryBased,
  mergeLibraryPayloadsIntoUnits,
  restoreLibraryUnitTitles,
  sortLibraryUnits,
  wizardItemsToLibraryUnits,
  type LibraryPayload,
} from "./cycleLibraryUi";

interface GlobalTemplateRow {
  id: number | string;
  title: string;
  expiry_years: number;
  expiry_months?: number | null;
  framework_cycle_id: string | null;
  deactivated: number;
  employee_count: number;
  is_library_only?: number;
  /** 1 = company admins may edit criteria for cycles linked to this template */
  template_editable_by_company?: number;
  days_until_deletable?: number | null;
  has_continuous_assignments?: number;
  latest_assignment_expiry?: string | null;
}

interface LibraryElement {
  id: string;
  title: string;
  demonstrate: string[];
  explain: string[];
}

interface LibraryUnit {
  id: string;
  title: string;
  elements: LibraryElement[];
}

interface LibraryCycle {
  id: string;
  name: string;
  units: LibraryUnit[];
}

function templateContentLocked(row: GlobalTemplateRow): boolean {
  return !row.deactivated && Number(row.employee_count ?? 0) > 0;
}

function formatExpiryLabel(ymd: string | null | undefined): string | null {
  const s = String(ymd ?? "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const dt = new Date(`${s}T00:00:00Z`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

function deleteCountdownCopy(row: GlobalTemplateRow): string | null {
  const assigned = Number(row.employee_count ?? 0);
  if (assigned <= 0) return null;
  const days = row.days_until_deletable;
  const continuous = Number(row.has_continuous_assignments ?? 0) === 1;
  const expiry = formatExpiryLabel(row.latest_assignment_expiry);
  if (days == null) {
    return continuous
      ? translateUi("Cannot delete while continuous assignments remain. Close those assignments first.")
      : translateUi("Cannot delete while employees are assigned.");
  }
  if (days === 0) {
    return continuous
      ? translateUi("Current period has ended — close remaining assignments to delete this template.")
      : translateUi("Last assignment period has ended — close remaining assignments to delete this template.");
  }
  const daysBit =
    days === 1
      ? translateUi("1 day left before this template can be deleted.")
      : translateUi("{{days}} days left before this template can be deleted.", { days });
  if (expiry) {
    return continuous
      ? `${daysBit} ${translateUi("Current period ends {{date}}. Continuous assignments must then be closed.", { date: expiry })}`
      : `${daysBit} ${translateUi("Last assignment ends {{date}}.", { date: expiry })}`;
  }
  return daysBit;
}

function DaysLeftMeter({
  row,
  isDarkMode,
  isMobile,
}: {
  row: GlobalTemplateRow;
  isDarkMode?: boolean;
  isMobile?: boolean;
}) {
  const assigned = Number(row.employee_count ?? 0);
  if (assigned <= 0) return null;
  const days = row.days_until_deletable;
  const due = days === 0;
  const unknown = days == null;
  const numberLabel = unknown ? "—" : String(days);
  const unitLabel =
    unknown
      ? translateUi("until closed")
      : days === 1
        ? translateUi("day left")
        : translateUi("days left");
  const expiry = formatExpiryLabel(row.latest_assignment_expiry);
  const title = deleteCountdownCopy(row) || undefined;
  const tone = due
    ? isDarkMode
      ? "border-amber-500/50 bg-amber-950/70 text-amber-100"
      : "border-amber-300 bg-amber-100 text-amber-950"
    : isDarkMode
      ? "border-orange-500/40 bg-orange-950/50 text-orange-100"
      : "border-orange-200 bg-white text-orange-950 shadow-sm";

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span
        className={cbCn(
          "text-[10px] font-semibold uppercase tracking-wider",
          isDarkMode ? "text-slate-400" : "text-slate-500"
        )}
      >
        {translateUi("Days left")}
      </span>
      <div
        title={title}
        className={cbCn(
          "inline-flex items-center gap-2 rounded-full border px-2.5 py-1",
          isMobile ? "min-h-[32px]" : "min-h-[26px]",
          tone
        )}
      >
        <Clock className={cbCn("h-3.5 w-3.5 shrink-0", due ? "opacity-90" : "opacity-80")} aria-hidden />
        <span className="text-[13px] font-bold tabular-nums leading-none">{numberLabel}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide leading-none">{unitLabel}</span>
        {expiry && !unknown ? (
          <span
            className={cbCn(
              "hidden text-[10px] font-medium leading-none sm:inline",
              isDarkMode ? "text-orange-200/80" : "text-orange-800/70"
            )}
          >
            · {expiry}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function newEditorClientKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `w-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function unitsToEditableItems(units: LibraryUnit[]): WizardCriteriaItem[] {
  return flattenLibraryUnitsToWizardItems(units).map((item) => ({
    ...item,
    wizardSource: "custom",
    clientKey: newEditorClientKey(),
  }));
}

function libraryUnitsToCycle(id: string, name: string, units: LibraryUnit[]): Cycle {
  return { id, name, gradingLevels: GRADING_LEVELS, units };
}

function SegmentedChoice({
  checked,
  onCheckedChange,
  disabled,
  isDarkMode,
  isMobile,
  offLabel,
  onLabel,
  caption,
  offTitle,
  onTitle,
  onTone = "indigo",
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean;
  isDarkMode?: boolean;
  isMobile?: boolean;
  offLabel: string;
  onLabel: string;
  caption: string;
  offTitle: string;
  onTitle: string;
  onTone?: "emerald" | "indigo";
}) {
  const selectedOn =
    onTone === "emerald"
      ? isDarkMode
        ? "bg-emerald-600 text-white shadow-sm"
        : "bg-emerald-600 text-white shadow-sm"
      : isDarkMode
        ? "bg-indigo-600 text-white shadow-sm"
        : "bg-indigo-600 text-white shadow-sm";
  const selectedOff = isDarkMode
    ? "bg-slate-600 text-slate-100 shadow-sm"
    : "bg-white text-slate-800 shadow-sm";
  const idle = isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800";
  const btn = (active: boolean, onSide: boolean) =>
    cbCn(
      "inline-flex items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none transition-colors",
      isMobile ? "min-h-[32px] min-w-[4.5rem]" : "min-h-[26px]",
      disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
      active ? (onSide ? selectedOn : selectedOff) : idle
    );

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span
        className={cbCn(
          "text-[10px] font-semibold uppercase tracking-wider",
          isDarkMode ? "text-slate-400" : "text-slate-500"
        )}
      >
        {caption}
      </span>
      <div
        role="group"
        aria-label={caption}
        className={cbCn(
          "inline-flex shrink-0 items-center rounded-full border p-0.5",
          isDarkMode ? "border-slate-600 bg-slate-900/70" : "border-slate-200 bg-slate-100/90"
        )}
      >
        <button
          type="button"
          aria-pressed={!checked}
          title={offTitle}
          disabled={disabled}
          onClick={() => {
            if (!disabled && checked) onCheckedChange(false);
          }}
          className={btn(!checked, false)}
        >
          {offLabel}
        </button>
        <button
          type="button"
          aria-pressed={checked}
          title={onTitle}
          disabled={disabled}
          onClick={() => {
            if (!disabled && !checked) onCheckedChange(true);
          }}
          className={btn(checked, true)}
        >
          {onLabel}
        </button>
      </div>
    </div>
  );
}

let globalTemplatesPaneCache: GlobalTemplateRow[] | null = null;

export function GlobalTemplatesPane({
  currentUserEmail,
  isMobile,
  isDarkMode = false,
  onMounted,
  onTemplatesChanged,
  embedded = false,
  onCountChange,
}: {
  currentUserEmail?: string | null;
  isMobile?: boolean;
  isDarkMode?: boolean;
  onMounted?: (refresh: () => void) => void;
  onTemplatesChanged?: () => void;
  /** Skip outer panel chrome when nested in CollapsiblePane. */
  embedded?: boolean;
  onCountChange?: (count: number) => void;
}) {
  const [rows, setRows] = useState<GlobalTemplateRow[]>(() => globalTemplatesPaneCache ?? []);
  const [loading, setLoading] = useState(() => globalTemplatesPaneCache === null);
  const [actioning, setActioning] = useState<string | number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<GlobalTemplateRow | null>(null);
  const [editingUnits, setEditingUnits] = useState<LibraryUnit[] | null>(null);
  const [editingItems, setEditingItems] = useState<WizardCriteriaItem[] | null>(null);
  const [editingLoading, setEditingLoading] = useState(false);
  const [editorMessage, setEditorMessage] = useState<string | null>(null);
  const [editorSelectedLibrary, setEditorSelectedLibrary] = useState<Record<string, LibraryPayload>>({});
  const [editorExpandedUnits, setEditorExpandedUnits] = useState<Record<string, boolean>>({});
  const [editorExpandedElements, setEditorExpandedElements] = useState<Record<string, boolean>>({});
  const [industryLibraryOpen, setIndustryLibraryOpen] = useState(false);
  const [viewingRow, setViewingRow] = useState<GlobalTemplateRow | null>(null);
  const [viewingUnits, setViewingUnits] = useState<LibraryUnit[] | null>(null);
  const [viewingLoading, setViewingLoading] = useState(false);
  const preventReopenUntilRef = useRef(0);

  useEffect(() => {
    if (!savedMessage) return;
    const t = setTimeout(() => setSavedMessage(null), 3000);
    return () => clearTimeout(t);
  }, [savedMessage]);

  const fetchGlobalTemplates = useCallback(async () => {
    if (globalTemplatesPaneCache === null) setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/cycles/global-list", {
        headers: currentUserEmail ? { "X-User-Email": currentUserEmail } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Failed to load global templates");
        return;
      }
      const nextRows = Array.isArray(data) ? data : [];
      globalTemplatesPaneCache = nextRows;
      setRows(nextRows);
      onCountChange?.(nextRows.length);
    } catch (err: any) {
      setError(err?.message || "Failed to load global templates");
    } finally {
      setLoading(false);
    }
  }, [currentUserEmail, onCountChange]);

  useEffect(() => {
    fetchGlobalTemplates();
  }, [fetchGlobalTemplates]);

  useEffect(() => {
    onMounted?.(fetchGlobalTemplates);
  }, [fetchGlobalTemplates, onMounted]);

  const closeContentEditor = useCallback(() => {
    setEditingRow(null);
    setEditingUnits(null);
    setEditingItems(null);
    setEditingLoading(false);
    setEditorMessage(null);
    setEditorSelectedLibrary({});
    setEditorExpandedUnits({});
    setEditorExpandedElements({});
    setIndustryLibraryOpen(false);
  }, []);

  const closeContentViewer = useCallback(() => {
    setViewingRow(null);
    setViewingUnits(null);
    setViewingLoading(false);
  }, []);

  const handleSuspendToggle = async (row: GlobalTemplateRow, activate: boolean) => {
    setActioning(row.id);
    setError(null);
    try {
      const isLibraryOnly = !!row.is_library_only;
      const fw = row.framework_cycle_id ? String(row.framework_cycle_id).trim() : "";
      const res = await fetch(
        isLibraryOnly && fw
          ? `/api/cycle-library/${encodeURIComponent(fw)}/suspended`
          : `/api/cycles/${row.id}/${activate ? "activate" : "deactivate"}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
          },
          body:
            isLibraryOnly && fw
              ? JSON.stringify({ deactivated: activate ? 0 : 1 })
              : undefined,
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Failed to ${activate ? "activate" : "suspend"} template`);
        return;
      }
      await fetchGlobalTemplates();
      onTemplatesChanged?.();
      setSavedMessage(
        activate
          ? translateUi("Template activated. It is available for new assignments.")
          : translateUi("Template suspended. Hidden from new companies; existing assignees keep the cycle.")
      );
    } catch (err: any) {
      setError(err?.message || `Failed to ${activate ? "activate" : "suspend"} template`);
    } finally {
      setActioning(null);
    }
  };

  const handleDelete = async (row: GlobalTemplateRow) => {
    if (
      !confirm(
        translateUi(
          "Delete global template \"{{title}}\"?\n\nThis is blocked if any employee in any company is assigned to this cycle.",
          { title: row.title },
        )
      )
    ) {
      return;
    }
    setActioning(row.id);
    setError(null);
    try {
      const isLibraryOnly = !!row.is_library_only;
      const frameworkId = row.framework_cycle_id ? String(row.framework_cycle_id).trim() : "";
      const res = await fetch(
        isLibraryOnly && frameworkId ? `/api/cycle-library/${encodeURIComponent(frameworkId)}` : `/api/cycles/${row.id}`,
        {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
        },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Failed to delete template");
        return;
      }
      await fetchGlobalTemplates();
      onTemplatesChanged?.();
      setSavedMessage("Template deleted.");
    } catch (err: any) {
      setError(err?.message || "Failed to delete template");
    } finally {
      setActioning(null);
    }
  };

  const handleRename = async (row: GlobalTemplateRow) => {
    const nextTitle = prompt("Rename global template", row.title)?.trim() ?? "";
    if (!nextTitle || nextTitle === row.title.trim()) return;
    setActioning(row.id);
    setError(null);
    try {
      const isLibraryOnly = !!row.is_library_only;
      const frameworkId = row.framework_cycle_id ? String(row.framework_cycle_id).trim() : "";
      const endpoint = isLibraryOnly && frameworkId
        ? `/api/cycle-library/${encodeURIComponent(frameworkId)}/rename`
        : `/api/cycles/${row.id}/rename-template`;
      const res = await fetch(endpoint, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
        },
        body: JSON.stringify({ title: nextTitle }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Failed to rename template");
        return;
      }
      await fetchGlobalTemplates();
      onTemplatesChanged?.();
      setSavedMessage("Template renamed.");
    } catch (err: any) {
      setError(err?.message || "Failed to rename template");
    } finally {
      setActioning(null);
    }
  };

  const handleSetCompanyEditable = async (row: GlobalTemplateRow, editable: boolean) => {
    const frameworkId = row.framework_cycle_id ? String(row.framework_cycle_id).trim() : "";
    if (!frameworkId) {
      setError("This template has no framework id — cannot set edit policy.");
      return;
    }
    setActioning(row.id);
    setError(null);
    try {
      const res = await fetch(`/api/cycle-library/${encodeURIComponent(frameworkId)}/company-editable`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
        },
        body: JSON.stringify({ template_editable_by_company: editable ? 1 : 0 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Failed to update template edit policy");
        return;
      }
      await fetchGlobalTemplates();
      onTemplatesChanged?.();
      setSavedMessage(editable ? translateUi("Template is editable by company admins.") : translateUi("Template is fixed (locked for companies)."));
    } catch (err: any) {
      setError(err?.message || "Failed to update template edit policy");
    } finally {
      setActioning(null);
    }
  };

  const handleDuplicate = async (row: GlobalTemplateRow) => {
    const suggested = `${row.title} Copy`;
    const title = prompt("Duplicate template as", suggested)?.trim() ?? "";
    if (!title) return;
    setActioning(row.id);
    setError(null);
    try {
      const isLibraryOnly = !!row.is_library_only;
      const frameworkId = row.framework_cycle_id ? String(row.framework_cycle_id).trim() : "";
      const endpoint = isLibraryOnly && frameworkId
        ? `/api/cycle-library/${encodeURIComponent(frameworkId)}/duplicate`
        : `/api/cycles/${row.id}/duplicate-template`;
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
        },
        body: JSON.stringify({ title }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Failed to duplicate template");
        return;
      }
      await fetchGlobalTemplates();
      onTemplatesChanged?.();
      setSavedMessage("Template duplicated.");
    } catch (err: any) {
      setError(err?.message || "Failed to duplicate template");
    } finally {
      setActioning(null);
    }
  };

  const fetchTemplateUnits = async (row: GlobalTemplateRow): Promise<LibraryUnit[] | null> => {
    const frameworkId = row.framework_cycle_id ? String(row.framework_cycle_id).trim() : "";
    if (!frameworkId) {
      setError("This template cannot be opened because it has no framework link.");
      return null;
    }
    const res = await fetch(`/api/cycle-library?v=${Date.now()}`, {
      cache: "no-store",
      headers: currentUserEmail ? { "X-User-Email": currentUserEmail } : {},
    });
    const data = await res.json().catch(() => []);
    if (!res.ok) {
      setError("Failed to load template content");
      return null;
    }
    const list = Array.isArray(data) ? (data as LibraryCycle[]) : [];
    const tpl = list.find((c) => String(c.id ?? "").trim() === frameworkId);
    if (!tpl) {
      setError("Template content not found.");
      return null;
    }
    return restoreLibraryUnitTitles(
      sortLibraryUnits(JSON.parse(JSON.stringify(tpl.units || [])), TRAIN_DRIVER_V1_CYCLE.units),
      TRAIN_DRIVER_V1_CYCLE.units
    );
  };

  const openContentEditor = async (row: GlobalTemplateRow) => {
    // Prevent click-through reopening right after a successful save close.
    if (Date.now() < preventReopenUntilRef.current) return;
    if (templateContentLocked(row)) {
      setError(
        translateUi(
          "Cannot edit units or criteria while this template is active and has assigned employees. Suspend it first, or duplicate to make a new version."
        )
      );
      return;
    }
    closeContentViewer();
    setEditingRow(row);
    setEditingUnits(null);
    setEditingItems(null);
    setEditingLoading(true);
    setError(null);
    setEditorMessage(null);
    setEditorSelectedLibrary({});
    setEditorExpandedUnits({});
    setEditorExpandedElements({});
    setIndustryLibraryOpen(false);
    try {
      const copied = await fetchTemplateUnits(row);
      if (!copied) {
        closeContentEditor();
        return;
      }
      setEditingUnits(copied);
      setEditingItems(unitsToEditableItems(copied));
    } catch (err: any) {
      setError(err?.message || "Failed to load template content");
      closeContentEditor();
    } finally {
      setEditingLoading(false);
    }
  };

  const openContentViewer = async (row: GlobalTemplateRow) => {
    if (Date.now() < preventReopenUntilRef.current) return;
    closeContentEditor();
    setViewingRow(row);
    setViewingUnits(null);
    setViewingLoading(true);
    setError(null);
    try {
      const copied = await fetchTemplateUnits(row);
      if (!copied) {
        closeContentViewer();
        return;
      }
      setViewingUnits(copied);
    } catch (err: any) {
      setError(err?.message || "Failed to load template content");
      closeContentViewer();
    } finally {
      setViewingLoading(false);
    }
  };

  const editorItems = editingItems ?? [];
  const editorKeyCounts = useMemo(() => buildTemplateLibraryKeyCounts(editorItems), [editorItems]);
  const isCustomTemplateEdit = useMemo(
    () => !libraryUnitsLookIndustryBased(editingUnits ?? [], TRAIN_DRIVER_V1_CYCLE.units),
    [editingUnits]
  );
  const editorStats = useMemo(() => countWizardCycleStats(editingItems ?? []), [editingItems]);
  const viewingStats = useMemo(
    () => countWizardCycleStats(flattenLibraryUnitsToWizardItems(viewingUnits ?? [])),
    [viewingUnits]
  );
  const viewingCycle = useMemo(() => {
    if (!viewingRow || !viewingUnits) return null;
    const frameworkId = viewingRow.framework_cycle_id ? String(viewingRow.framework_cycle_id).trim() : "";
    return libraryUnitsToCycle(frameworkId || String(viewingRow.id), viewingRow.title, viewingUnits);
  }, [viewingRow, viewingUnits]);

  const clearEditorSelected = useCallback((keys?: string[]) => {
    if (!keys || keys.length === 0) {
      setEditorSelectedLibrary({});
      return;
    }
    setEditorSelectedLibrary((prev) => {
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    });
  }, []);

  const setEditorPayloadsSelected = useCallback((payloads: LibraryPayload[], selected: boolean) => {
    setEditorSelectedLibrary((prev) => {
      const next = { ...prev };
      for (const p of payloads) {
        if (!p.libraryKey) continue;
        if (selected) next[p.libraryKey] = p;
        else delete next[p.libraryKey];
      }
      return next;
    });
  }, []);

  const mergeEditorPayloads = useCallback((payloads: LibraryPayload[], forceType?: "demonstrate" | "explain") => {
    if (payloads.length === 0) return;
    const fromItems = wizardItemsToLibraryUnits(editingItems ?? [], {
      dropEmpty: false,
      catalogue: TRAIN_DRIVER_V1_CYCLE.units,
    });
    const next = mergeLibraryPayloadsIntoUnits(fromItems, payloads, TRAIN_DRIVER_V1_CYCLE.units, forceType);
    setEditingUnits(next);
    setEditingItems(unitsToEditableItems(next));
    clearEditorSelected(payloads.map((p) => p.libraryKey).filter((k): k is string => !!k));
  }, [clearEditorSelected, editingItems]);

  const addEditorSelectedTo = useCallback(
    (unitPayloads: LibraryPayload[], type: "demonstrate" | "explain") => {
      const picked = unitPayloads.filter((p) => p.libraryKey && editorSelectedLibrary[p.libraryKey]);
      if (picked.length === 0) return;
      mergeEditorPayloads(picked, type);
    },
    [editorSelectedLibrary, mergeEditorPayloads]
  );

  const saveContentEditor = async () => {
    if (!editingRow || (!editingUnits && !editingItems)) return;
    if (templateContentLocked(editingRow)) {
      const msg = translateUi(
        "Cannot edit units or criteria while this template is active and has assigned employees. Suspend it first, or duplicate to make a new version."
      );
      setError(msg);
      setEditorMessage(msg);
      return;
    }
    const frameworkId = editingRow.framework_cycle_id ? String(editingRow.framework_cycle_id).trim() : "";
    if (!frameworkId) return;
    const unitsToSave = sortLibraryUnits(
      wizardItemsToLibraryUnits(editingItems ?? [], {
        dropEmpty: true,
        catalogue: isCustomTemplateEdit ? [] : TRAIN_DRIVER_V1_CYCLE.units,
      }),
      TRAIN_DRIVER_V1_CYCLE.units
    );
    setActioning(editingRow.id);
    setError(null);
    setEditorMessage(null);
    try {
      const res = await fetch(`/api/cycle-library/${encodeURIComponent(frameworkId)}/content`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
        },
        body: JSON.stringify({ units: unitsToSave }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error || "Failed to save template content";
        setError(msg);
        setEditorMessage(`Save failed: ${msg}`);
        return;
      }
      preventReopenUntilRef.current = Date.now() + 700;
      closeContentEditor();
      setSavedMessage("Template content saved.");
      await fetchGlobalTemplates();
      try {
        onTemplatesChanged?.();
      } catch {
        // Keep success UX even if parent refresh callback throws.
      }
    } catch (err: any) {
      const msg = err?.message || "Failed to save template content";
      setError(msg);
      setEditorMessage(`Save failed: ${msg}`);
    } finally {
      setActioning(null);
    }
  };

  const body = (
    <>
      {!embedded && (
        <>
          <h3 className={cb.heading(isDarkMode, !isMobile)}>{translateUi("Global Templates")}</h3>
          <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
            {translateUi("Global cycles used across all companies.")}
          </p>
        </>
      )}
      {error && (
        <div className={cbCn("mt-2 rounded-lg border px-3 py-2 text-sm", cb.alertError(isDarkMode))}>
          {error}
        </div>
      )}
      {savedMessage && (
        <div className={cbCn("mt-2 rounded-lg border px-3 py-2 text-sm", cb.alertSuccess(isDarkMode))}>
          {savedMessage}
        </div>
      )}
      {loading ? (
        <div className={cbCn("mt-4 py-8 text-center text-sm", cb.bodySm(isDarkMode))}>{translateUi("Loading…")}</div>
      ) : rows.length === 0 ? (
        <div className={cbCn("mt-4 py-8 text-center text-sm", cb.bodySm(isDarkMode))}>
          {translateUi("No global templates found.")}
        </div>
      ) : (
        <ul className={`mt-4 space-y-2 overflow-y-auto ${isMobile ? "max-h-[280px]" : "max-h-[360px]"}`}>
          {rows.map((r) => (
            <li
              key={String(r.id)}
              className={cbCn(
                "rounded-lg border text-sm",
                isMobile ? "px-4 py-3" : "px-3 py-2.5",
                cb.listRow(isDarkMode, !!r.deactivated)
              )}
            >
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className={cbCn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-800")}>{r.title}</span>
                  <button
                    type="button"
                    onClick={() => handleRename(r)}
                    disabled={actioning === r.id}
                    className={cbCn(
                      "rounded p-1",
                      isDarkMode ? "text-slate-400 hover:bg-slate-700 hover:text-slate-100" : "text-slate-500 hover:bg-slate-200 hover:text-slate-800",
                      isMobile ? "min-h-[36px] min-w-[36px]" : ""
                    )}
                    title={translateUi("Rename template")}
                    aria-label={translateUi("Rename template")}
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  <span className={cbCn("shrink-0", cb.bodySm(isDarkMode))}>
                    ({formatCycleDurationLabel(resolveDisplayedCycleMonths(r))})
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5" aria-label={translateUi("Template tags")}>
                    <span className={cb.tag(isDarkMode, "indigo")}>{translateUi("Global")}</span>
                    {r.is_library_only ? (
                      <span className={cb.tag(isDarkMode, "slate")}>{translateUi("Library")}</span>
                    ) : null}
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
                    <DaysLeftMeter row={r} isDarkMode={isDarkMode} isMobile={isMobile} />
                    <SegmentedChoice
                      checked={!r.deactivated}
                      onCheckedChange={(next) => handleSuspendToggle(r, next)}
                      disabled={actioning === r.id}
                      isDarkMode={isDarkMode}
                      isMobile={isMobile}
                      caption={translateUi("Availability")}
                      offLabel={translateUi("Suspended")}
                      onLabel={translateUi("Active")}
                      offTitle={translateUi("Suspend – no new assignments; current employees continue")}
                      onTitle={translateUi("Active — available for new assignments")}
                      onTone="emerald"
                    />
                    {r.framework_cycle_id ? (
                      <SegmentedChoice
                        checked={Number(r.template_editable_by_company ?? 0) === 1}
                        onCheckedChange={(next) => handleSetCompanyEditable(r, next)}
                        disabled={actioning === r.id}
                        isDarkMode={isDarkMode}
                        isMobile={isMobile}
                        caption={translateUi("Company edits")}
                        offLabel={translateUi("Fixed")}
                        onLabel={translateUi("Editable")}
                        offTitle={translateUi("Fixed — company admins cannot change criteria")}
                        onTitle={translateUi("Editable — company admins may customise criteria")}
                        onTone="indigo"
                      />
                    ) : null}
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openContentViewer(r)}
                        disabled={actioning === r.id || !r.framework_cycle_id}
                        className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "violet"))}
                        title={translateUi("Compact view (read only)")}
                        aria-label={translateUi("Compact view (read only)")}
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openContentEditor(r)}
                        disabled={actioning === r.id || !r.framework_cycle_id || templateContentLocked(r)}
                        className={cbCn(
                          cb.iconBtn(isDarkMode, isMobile),
                          cb.iconBtnHover(isDarkMode, "indigo"),
                          "disabled:cursor-not-allowed disabled:opacity-50"
                        )}
                        title={
                          templateContentLocked(r)
                            ? translateUi(
                                "Cannot edit units or criteria while this template is active and has assignees. Suspend it first, or duplicate to make a new version."
                              )
                            : translateUi("Edit units and criteria")
                        }
                        aria-label={translateUi("Edit units and criteria")}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDuplicate(r)}
                        disabled={actioning === r.id}
                        className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "sky"))}
                        title={translateUi("Duplicate template")}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(r)}
                        disabled={r.employee_count > 0 || actioning === r.id}
                        className={cbCn(
                          cb.iconBtn(isDarkMode, isMobile),
                          cb.iconBtnHover(isDarkMode, "red"),
                          "disabled:cursor-not-allowed disabled:opacity-50"
                        )}
                        title={
                          r.employee_count > 0
                            ? deleteCountdownCopy(r) ||
                              "Cannot delete – employees assigned in one or more companies"
                            : r.is_library_only
                              ? translateUi("Delete library template")
                              : translateUi("Delete global template")
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <p className={cbCn("mt-1.5 text-xs", cb.bodySm(isDarkMode))}>
                  {r.employee_count} employee{r.employee_count !== 1 ? "s" : ""} assigned (all companies)
                  {Number(r.has_continuous_assignments ?? 0) === 1
                    ? ` · ${translateUi("Includes continuous assignments — close them after the period ends to delete.")}`
                    : ""}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
      {editingRow ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-6">
          <div
            className={cbCn(
              `my-auto w-full max-w-[110rem] ${isMobile ? "p-4" : "p-6"}`,
              cb.modal(isDarkMode)
            )}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-template-content-title"
          >
            <div className="flex items-start justify-between gap-3">
              <h4 id="edit-template-content-title" className={cbCn("min-w-0 text-lg font-semibold", cb.heading(isDarkMode, true))}>
                Edit template content: {editingRow.title}
              </h4>
              <div className="flex shrink-0 items-center gap-2">
                <CycleCountsBadge units={editorStats.units} criteria={editorStats.criteria} isDarkMode={isDarkMode} />
                <button
                  type="button"
                  onClick={closeContentEditor}
                  className={cbCn(cb.iconBtn(isDarkMode, isMobile))}
                  aria-label={translateUi("Close edit template popup")}
                  title={translateUi("Close")}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
              {isCustomTemplateEdit
                ? translateUi(
                    "Edit units and criteria. Each Demonstrate and Explain row has its own element number and optional title."
                  )
                : translateUi("Add or restore criteria from the industry-standard library, or edit lines already on this template.")}
            </p>
            {editorMessage ? (
              <div className={cbCn("mt-3 rounded-lg border px-3 py-2 text-sm", cb.alertError(isDarkMode))}>
                {editorMessage}
              </div>
            ) : null}
            {editingLoading ? (
              <div className={cbCn("mt-4 text-sm", cb.bodySm(isDarkMode))}>{translateUi("Loading template content…")}</div>
            ) : editingItems == null ? (
              <div className={cbCn("mt-4 text-sm", cb.bodySm(isDarkMode))}>{translateUi("No template content loaded.")}</div>
            ) : (
              <div className="mt-4 flex min-w-0 flex-col gap-4">
                {isCustomTemplateEdit ? null : (
                  <CollapsiblePane
                    open={industryLibraryOpen}
                    onToggle={() => setIndustryLibraryOpen((v) => !v)}
                    title={translateUi("Industry standard units")}
                    summary={
                      industryLibraryOpen
                        ? translateUi("Pick criteria to add")
                        : translateUi("Expand to add from the library")
                    }
                    isDarkMode={isDarkMode}
                    isMobile={isMobile}
                    pad="sm"
                  >
                    <IndustryStandardUnitsPane
                      items={editorItems}
                      keyCounts={editorKeyCounts}
                      selectedLibrary={editorSelectedLibrary}
                      isMobile={isMobile}
                      compactTiles
                      tapToAdd
                      hideHeader
                      horizontalTiles
                      isDarkMode={isDarkMode}
                      demoLabel={translateUi("Demonstrate")}
                      explainLabel={translateUi("Explain")}
                      expandedUnits={editorExpandedUnits}
                      expandedElements={editorExpandedElements}
                      onToggleUnit={(key) => setEditorExpandedUnits((prev) => ({ ...prev, [key]: !prev[key] }))}
                      onToggleElement={(key) => setEditorExpandedElements((prev) => ({ ...prev, [key]: !prev[key] }))}
                      onToggleLine={(payload) => {
                        const key = payload.libraryKey;
                        if (!key) return;
                        setEditorSelectedLibrary((prev) => {
                          if (prev[key]) {
                            const next = { ...prev };
                            delete next[key];
                            return next;
                          }
                          return { ...prev, [key]: payload };
                        });
                      }}
                      onSetPayloadsSelected={setEditorPayloadsSelected}
                      onClearSelected={clearEditorSelected}
                      onAddWhole={mergeEditorPayloads}
                      onAddSelectedTo={addEditorSelectedTo}
                      onAddLine={(payload, type) => mergeEditorPayloads([payload], type)}
                      onLibraryDragStart={() => {}}
                      onLibraryDragEnd={() => {}}
                      getDragLibraryItems={(payload) => {
                        const key = payload.libraryKey;
                        if (!key || !editorSelectedLibrary[key]) return [payload];
                        const selectedItems = Object.values(editorSelectedLibrary);
                        return selectedItems.length > 0 ? selectedItems : [payload];
                      }}
                    />
                  </CollapsiblePane>
                )}
                <div className="min-w-0">
                  <h5 className={cbCn("mb-2 text-sm font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                    {translateUi("On this template")}
                  </h5>
                  <CustomCycleCriteriaEditor
                    items={editingItems}
                    onChange={setEditingItems}
                    demoLabel={translateUi("Demonstrate")}
                    explainLabel={translateUi("Explain")}
                    isDarkMode={isDarkMode}
                    isMobile={isMobile}
                  />
                </div>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeContentEditor}
                className={cbCn("rounded border px-3 py-2 text-sm", cb.btnSecondary(isDarkMode))}
              > {translateUi("Cancel")} </button>
              <button
                type="button"
                onClick={saveContentEditor}
                disabled={editingItems == null || actioning === editingRow.id}
                className="rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {actioning === editingRow.id ? translateUi("Saving...") : translateUi("Save changes")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {viewingRow ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-6">
          <div
            className={cbCn(`my-auto w-full max-w-4xl ${isMobile ? "p-4" : "p-6"}`, cb.modal(isDarkMode))}
            role="dialog"
            aria-modal="true"
            aria-labelledby="view-template-content-title"
          >
            <div className="flex items-start justify-between gap-3">
              <h4 id="view-template-content-title" className={cbCn("min-w-0 text-lg font-semibold", cb.heading(isDarkMode, true))}>
                {translateUi("Template view")}: {viewingRow.title}
              </h4>
              <div className="flex shrink-0 items-center gap-2">
                <CycleCountsBadge units={viewingStats.units} criteria={viewingStats.criteria} isDarkMode={isDarkMode} />
                <button
                  type="button"
                  onClick={closeContentViewer}
                  className={cbCn(cb.iconBtn(isDarkMode, isMobile))}
                  aria-label={translateUi("Close template view")}
                  title={translateUi("Close")}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
              {translateUi("Compact read-only view of units and criteria.")}
            </p>
            {viewingLoading ? (
              <div className={cbCn("mt-4 text-sm", cb.bodySm(isDarkMode))}>{translateUi("Loading template content…")}</div>
            ) : viewingCycle == null ? (
              <div className={cbCn("mt-4 text-sm", cb.bodySm(isDarkMode))}>{translateUi("No template content loaded.")}</div>
            ) : (
              <div className="mt-4">
                <CycleStructureView cycle={viewingCycle} defaultCollapsed isDarkMode={isDarkMode} />
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={closeContentViewer}
                className={cbCn("rounded border px-3 py-2 text-sm", cb.btnSecondary(isDarkMode))}
              >
                {translateUi("Close")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );

  if (embedded) return <div className={isMobile ? "p-4" : "p-5 sm:p-6"}>{body}</div>;
  return <div className={cb.panel(isDarkMode, isMobile ? "sm" : "md")}>{body}</div>;
}
